import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  EqueuePrintDto,
  FiscalConfig,
  FiscalConfigInDto,
  FiscalOutOfServiceDto,
  FullStatus,
  IFiscalProtocol,
  HealthResponse,
  PrinterDto,
  PrinterStatus,
  PrintJobDto,
  ZReportResult,
} from '@h5s/dtos-api';
import {
  CashRegisterReceipt,
  mapCashRegisterReceiptToZPrinterDocument,
  UpsertZPrinterConfiguration,
  ZPrinterConfiguration,
  ZPrinterFiscalConfigRequest,
  ZPrinterFiscalProtocol,
  ZPrinterFullStatus,
  ZPrinterPrintJob,
  ZPrinterStatus,
  CashRegisterCreditNote,
  mapCashRegisterCreditNoteToZPrinterDocument,
  ZPrinterFiscalConfig,
  ZPrinterHealthResponse,
  ZPrinterZReportResult,
  FiscalOutOfServiceRequest,
  ZPrinterManufacturer,
} from '@h5s/entities';
import { AppErrorBuilder } from '@h5s/util-error';
import { mapArrayResponse, mapParamDto, mapResponse } from '@h5s/util-object';
import { map, Observable, of, switchMap } from 'rxjs';

@Service()
export class DataAccessZPrinterService {
  private readonly http = inject(HttpClient);

  private readonly API_BASE_PATH = '/api/printer';

  /*
   * Sincronizza la configurazione della stampante, se esiste aggiorna, altrimenti crea una nuova configurazione
   */
  public syncPrinter(
    payload: UpsertZPrinterConfiguration,
    printers?: ZPrinterConfiguration[],
  ): Observable<ZPrinterConfiguration> {
    if (!payload.id) {
      throw new AppErrorBuilder(`[DataAccessZPrinterService] syncPrinter requires id`)
        .addInfo('Payload:', JSON.stringify(payload))
        .build();
    }

    // Observable che rappresenta la lista di stampanti
    const printers$ = printers ? of(printers) : this.getPrinters();

    return printers$.pipe(
      switchMap((list) => {
        const printer = list.find((p) => p.id === payload.id);

        payload.row_version = printer ? printer.row_version : 0; // Se la stampante esiste, usa il suo row_version, altrimenti inizia da 0

        return this.updatePrinter(payload);
      }),
    );
  }

  /**
   * Recupera la lista delle stampanti configurate
   */
  public getPrinters(): Observable<ZPrinterConfiguration[]> {
    return this.http
      .get<PrinterDto[]>(`${this.API_BASE_PATH}`)
      .pipe(map((result) => mapArrayResponse(ZPrinterConfiguration, result)));
  }

  /**
   * Recupera una singola stampante per ID
   */
  public getPrinterById(id: string): Observable<ZPrinterConfiguration> {
    return this.http
      .get<PrinterDto>(`${this.API_BASE_PATH}/${id}`)
      .pipe(map((result) => mapResponse(ZPrinterConfiguration, result)));
  }

  /**
   * Crea una nuova stampante
   */
  public createPrinter(createPrinter: UpsertZPrinterConfiguration): Observable<ZPrinterConfiguration> {
    const upsertZPrinterConfigurationDto = mapParamDto<PrinterDto>(createPrinter);
    return this.http
      .post<PrinterDto>(`${this.API_BASE_PATH}`, upsertZPrinterConfigurationDto)
      .pipe(map((result) => mapResponse(ZPrinterConfiguration, result)));
  }

  /**
   * Aggiorna una stampante esistente per ID o crea una nuova stampante se non esiste
   */
  public updatePrinter(updatePrinter: UpsertZPrinterConfiguration): Observable<ZPrinterConfiguration> {
    if (!updatePrinter.id) {
      throw new AppErrorBuilder(`[DataAccessZPrinterService] updatePrinter requires id`)
        .addInfo('Payload:', JSON.stringify(updatePrinter))
        .build();
    }

    const upsertZPrinterConfigurationDto = mapParamDto<PrinterDto>(updatePrinter);
    return this.http
      .put<PrinterDto>(`${this.API_BASE_PATH}/${updatePrinter.id}`, upsertZPrinterConfigurationDto)
      .pipe(map((result) => mapResponse(ZPrinterConfiguration, result)));
  }

  /**
   * Elimina una singola stampante per ID
   */
  public deletePrinterById(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_PATH}/${id}`);
  }

  /**
   * Esegue una stampa non fiscale
   */
  public enqueuePrint(printerIdOrIp: string, payload: EqueuePrintDto): Observable<ZPrinterPrintJob> {
    return this.http
      .post<PrintJobDto>(`${this.API_BASE_PATH}/${printerIdOrIp}/enqueue-print`, payload)
      .pipe(map((result) => mapResponse(ZPrinterPrintJob, result)));
  }

  /**
   * Esegue una stampa fiscale
   */
  public enqueueDocument(
    printerIdOrIp: string,
    cashRegisterReceipt: CashRegisterReceipt,
    manufacturer: ZPrinterManufacturer,
  ): Observable<ZPrinterPrintJob> {
    // Mappatura di CashRegisterReceipt => Document
    const document = mapCashRegisterReceiptToZPrinterDocument(cashRegisterReceipt, manufacturer);
    return this.http
      .post<PrintJobDto>(`${this.API_BASE_PATH}/${printerIdOrIp}/enqueue-document`, document)
      .pipe(map((result) => mapResponse(ZPrinterPrintJob, result)));
  }

  public enqueueCreditNote(
    printerIdOrIp: string,
    cashRegisterReceipt: CashRegisterCreditNote,
  ): Observable<ZPrinterPrintJob> {
    // Mappatura di CashRegisterReceipt => Document
    const document = mapCashRegisterCreditNoteToZPrinterDocument(cashRegisterReceipt, printerIdOrIp);
    return this.http
      .post<ZPrinterPrintJob>(`${this.API_BASE_PATH}/${printerIdOrIp}/enqueue-document`, document)
      .pipe(map((result) => mapResponse(ZPrinterPrintJob, result)));
  }

  /**
   * Recupera lo stato completo di una stampante per ID
   */
  public status(printerIdOrIp: string): Observable<ZPrinterFullStatus> {
    return this.http
      .get<FullStatus>(`${this.API_BASE_PATH}/${printerIdOrIp}/status`)
      .pipe(map((result) => mapResponse(ZPrinterFullStatus, result)));
  }

  /**
   * Recupera lo stato di stampa di una stampante per ID
   */
  public printerStatus(printerIdOrIp: string): Observable<ZPrinterStatus> {
    return this.http
      .get<PrinterStatus>(`${this.API_BASE_PATH}/${printerIdOrIp}/printer-status`)
      .pipe(map((result) => mapResponse(ZPrinterStatus, result)));
  }

  /**
   * Ristampa l'ultimo documento stampato
   */
  public reprintLastDocument(printerIdOrIp: string): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_PATH}/${printerIdOrIp}/reprint-last-document`, {});
  }

  /**
   * Esegue una stampa di test per verificare la connessione con la stampante
   */
  public enqueueTestPrint(printerIdOrIp: string): Observable<ZPrinterPrintJob> {
    const payload: EqueuePrintDto = {
      print_header: false,
      cut_paper: true,
      rows: [{ text: '*** TEST PRINT ***' }, { text: 'Printer connection OK' }],
    };

    return this.enqueuePrint(printerIdOrIp, payload);
  }

  /**
   * Programma la configurazione fiscale sulla cassa.
   */
  public setFiscalConfig(
    printerIdOrIp: string,
    fiscalConfigRequest: ZPrinterFiscalConfigRequest,
  ): Observable<ZPrinterFiscalProtocol[]> {
    // Niente ignoreUndefined globale: i campi opzionali sono marcati @MappedPropertyOptional uno per uno,
    // così il controllo dev-mode sui campi obbligatori del payload resta attivo
    const dto = mapParamDto<FiscalConfigInDto>(fiscalConfigRequest);

    return this.http
      .post<IFiscalProtocol[]>(`${this.API_BASE_PATH}/${printerIdOrIp}/fiscal-config`, dto)
      .pipe(map((result) => mapArrayResponse(ZPrinterFiscalProtocol, result)));
  }

  /**
   * Recupera le configurazioni fiscali di una stampante per ID
   */
  public getFiscalConfig(printerIdOrIp: string): Observable<ZPrinterFiscalConfig> {
    return this.http
      .get<FiscalConfig>(`${this.API_BASE_PATH}/${printerIdOrIp}/fiscal-config`)
      .pipe(map((result) => mapResponse(ZPrinterFiscalConfig, result)));
  }

  /**
   * Esegue la chiusura fiscale di una stampante per ID
   */
  public sendClosure(printerIdOrIp: string): Observable<ZPrinterZReportResult> {
    return this.http
      .post<ZReportResult>(`${this.API_BASE_PATH}/${printerIdOrIp}/z-report`, {})
      .pipe(map((result) => mapResponse(ZPrinterZReportResult, result)));
  }

  /**
   * Esegue l'invio forzato delle pendenze che non sono state inviate di una stampante per ID
   */
  public forcePending(printerIdOrIp: string): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_PATH}/${printerIdOrIp}/force-pending-xml`, {});
  }

  /**
   * Recupera lo stato di salute del servizio z-printer
   */
  public health(): Observable<ZPrinterHealthResponse> {
    return this.http
      .get<HealthResponse>('/api/health')
      .pipe(map((result) => mapResponse(ZPrinterHealthResponse, result)));
  }

  /**
   * Esegue l'invio del fuori servizio di una stampante per ID
   */
  public sendOutOfService(
    printerIdOrIp: string,
    fiscalOutOfServiceRequest: FiscalOutOfServiceRequest,
  ): Observable<void> {
    const params = mapParamDto<FiscalOutOfServiceDto>(fiscalOutOfServiceRequest);
    return this.http.post<void>(`${this.API_BASE_PATH}/${printerIdOrIp}/out-of-service`, params);
  }

  /**
   * Recupera il numero di versione dello ZPrinter
   */
  public getVersion(): Observable<string> {
    return this.http.get<string>('/api/settings/version');
  }
}
